// ABOUTME: Rich text editor input component for service desk chat.
// ABOUTME: Uses TipTap for formatting (bold, italic), attachments, and future @ mentions support.

import { Box, IconButton } from "@mui/material";
import PopoverModal from "../../../../../../components/theme/Modal/PopoverModal";
import { ActionButton } from "../../../../../../components/global/Modals/ActionModal/components/ActionButton";
import Text from "../../../../../../components/theme/text/Text";
import { useEditor, EditorContent } from "@tiptap/react";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import TiptapText from "@tiptap/extension-text";
import Italic from "@tiptap/extension-italic";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MPhotoViewer from "../../../../../../components/global/Modals/MPhotoViewer";
import FontSize from "./extensions/FontSize";
import FontWeight from "./extensions/FontWeight";
import ResetStylesOnEnter from "./extensions/ResetStylesOnEnter";

const ChatInput = ({ onSend, onDataChange, hideSend, allowResolveOnSend, colors, theme, placeholder, viewer = 'fulfilmentcenter' }) => {
    const { t } = useTranslation();
    const [attachments, setAttachments] = useState([]);
    const [isItalic, setIsItalic] = useState(false);
    const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [sizeMenuAnchor, setSizeMenuAnchor] = useState(null);
    const [colorMenuAnchor, setColorMenuAnchor] = useState(null);
    const [weightMenuAnchor, setWeightMenuAnchor] = useState(null);
    const [sendModeAnchor, setSendModeAnchor] = useState(null);
    const [sendActionAnchor, setSendActionAnchor] = useState(null);
    const [currentSize, setCurrentSize] = useState("normal");
    const [currentColor, setCurrentColor] = useState("primary");
    const [currentWeight, setCurrentWeight] = useState("normal");
    // false = public reply (default), true = internal note (only visible to your side)
    const [isInternal, setIsInternal] = useState(false);

    // Filter only image attachments for the photo viewer
    const imageAttachments = useMemo(() => {
        return attachments
            .map((attachment, originalIndex) => ({ ...attachment, originalIndex }))
            .filter((attachment) => attachment.type === "image" && attachment.previewUrl);
    }, [attachments]);

    // Map image attachments to the format expected by MPhotoViewer
    const photosForViewer = useMemo(() => {
        return imageAttachments.map((attachment) => ({
            id: attachment.originalIndex,
            url: attachment.previewUrl,
            fileName: attachment.fileName,
        }));
    }, [imageAttachments]);

    const handleImageClick = useCallback((attachmentIndex) => {
        // Find the index in the imageAttachments array
        const imageIndex = imageAttachments.findIndex(
            (img) => img.originalIndex === attachmentIndex
        );
        if (imageIndex !== -1) {
            setCurrentPhotoIndex(imageIndex);
            setPhotoViewerOpen(true);
        }
    }, [imageAttachments]);

    const handleRemoveAttachment = useCallback((index) => {
        setAttachments((prev) => {
            const newAttachments = [...prev];
            if (newAttachments[index].previewUrl) {
                URL.revokeObjectURL(newAttachments[index].previewUrl);
            }
            newAttachments.splice(index, 1);
            return newAttachments;
        });
    }, []);

    const handlePhotoViewerDelete = useCallback((photoId) => {
        // photoId is the originalIndex in the attachments array
        handleRemoveAttachment(photoId);
        // If this was the last photo, close the modal
        if (imageAttachments.length === 1) {
            setPhotoViewerOpen(false);
        } else if (currentPhotoIndex >= imageAttachments.length - 1) {
            // If deleting the last photo in the viewer, move to previous
            setCurrentPhotoIndex(currentPhotoIndex - 1);
        }
    }, [handleRemoveAttachment, imageAttachments.length, currentPhotoIndex]);

    const editor = useEditor({
        extensions: [
            Document,
            Paragraph,
            TiptapText,
            Italic,
            Placeholder.configure({
                placeholder: placeholder || t("type_message") || "Typ een bericht...",
            }),
            TextStyle,
            Color,
            FontSize,
            FontWeight,
            ResetStylesOnEnter,
        ],
        content: "",
        editorProps: {
            attributes: {
                class: "chat-input-editor",
            },
        },
        onUpdate: ({ editor }) => {
            setIsItalic(editor.isActive("italic"));
            updateCurrentStyles(editor);
        },
        onSelectionUpdate: ({ editor }) => {
            setIsItalic(editor.isActive("italic"));
            updateCurrentStyles(editor);
        },
    });

    const updateCurrentStyles = useCallback((editor) => {
        const attrs = editor.getAttributes("textStyle");
        setCurrentSize(attrs.fontSize || "normal");
        setCurrentColor(attrs.color === colors.txt["secondary"] ? "secondary" : "primary");
        setCurrentWeight(attrs.fontWeight || "normal");
    }, [colors]);

    const handleSizeSelect = useCallback((size) => {
        // Always use setFontSize for all sizes including "normal"
        // This ensures stored marks are updated on empty lines
        editor?.chain().focus().setFontSize(size).run();
        setSizeMenuAnchor(null);
        setCurrentSize(size);
    }, [editor]);

    const handleColorSelect = useCallback((color) => {
        // For primary, unset the color (use inherited text color)
        // For secondary, set the specific color value
        if (color === "primary") {
            editor?.chain().focus().unsetColor().run();
        } else {
            editor?.chain().focus().setColor(colors.txt["secondary"]).run();
        }
        setColorMenuAnchor(null);
        setCurrentColor(color);
    }, [editor, colors]);

    const handleWeightSelect = useCallback((weight) => {
        // Always use setFontWeight for all weights including "normal"
        // This ensures stored marks are updated on empty lines
        editor?.chain().focus().setFontWeight(weight).run();
        setWeightMenuAnchor(null);
        setCurrentWeight(weight);
    }, [editor]);

    const handleSend = useCallback((resolveAfter) => {
        if (!editor) return;

        const content = editor.getJSON();
        const isEmpty = editor.isEmpty;

        if (isEmpty && attachments.length === 0) return;

        onSend?.({
            content: isEmpty ? null : content,
            attachments: attachments.length > 0 ? attachments : null,
            isInternal,
            resolveAfter: resolveAfter === true,
        });

        editor.commands.clearContent();
        setAttachments([]);
        setIsInternal(false);
    }, [editor, attachments, onSend, isInternal]);

    const handleClear = useCallback(() => {
        if (!editor) return;
        editor.commands.clearContent();
        setAttachments([]);
    }, [editor]);

    const handleAttachmentClick = useCallback(() => {
        const input = document.createElement("input");
        input.type = "file";
        input.multiple = true;
        input.onchange = (e) => {
            const files = Array.from(e.target.files);
            const newAttachments = files.map((file) => ({
                file,
                fileName: file.name,
                type: file.type.startsWith("image/") ? "image" : "document",
                previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
            }));
            setAttachments((prev) => [...prev, ...newAttachments]);
        };
        input.click();
    }, []);

    const toggleItalic = useCallback(() => {
        editor?.chain().focus().toggleItalic().run();
    }, [editor]);

    useEffect(() => {
        if (!editor) return;
        onDataChange?.({
            content: editor.isEmpty ? null : editor.getJSON(),
            attachments: attachments.length > 0 ? attachments : null,
            isInternal,
        });
    }, [editor, attachments, onDataChange, isInternal]);

    if (!editor) return null;

    // The public-send label depends on which side is writing. The fulfilmentcenter
    // is answering the customer ("Public reply"), whereas a client message is neither
    // an answer nor a question — just a public message ("Public").
    const publicLabel = viewer === 'client' ? t("public") : t("public_reply");
    const sendModeLabel = isInternal ? t("internal_note") : publicLabel;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                overflow: "hidden",
                position: "relative",
                width:1,
                // ...(isInternal && {
                //     "&::before": {
                //         content: "''",
                //         position: "absolute",
                //         inset: -16,
                //         // background: theme.palette.mode === "dark" ? "rgba(250, 204, 21, 0.06)" : colors.yellow[100],
                //         opacity: theme.palette.mode === "dark" ? 1 : 0.4,
                //         pointerEvents: "none",
                //         zIndex: 0,
                //     },
                // }),
            }}
        >
            {/* Send mode pill - absolute top-right; attachments wrap below it */}
            <Box
                onClick={(e) => setSendModeAnchor(e.currentTarget)}
                sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    zIndex: 2,
                    cursor: "pointer",
                    height: 32,
                    px: 1.5,
                    borderRadius: 4,
                    display: "flex",
                    alignItems: "center",
                    background: isInternal
                        ? colors.yellow[100]
                        : (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]),
                    color: isInternal
                        ? colors.yellow[400]
                        : colors.txt['primary'],
                    "&:hover": {
                        background: isInternal
                            ? colors.yellow[200]
                            : (theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200]),
                    },
                }}
            >
                {isInternal
                    ? <LockIcon sx={{ fontSize: 14 }} />
                    : <PublicIcon sx={{ fontSize: 14 }} />}
                <Text fontSize={13} semibold sx={{ color: "inherit", ml:1,mr:1.5, }}>{sendModeLabel}</Text>
                <KeyboardArrowUpIcon sx={{ fontSize: 16, color:colors.txt['secondary'] }} />
            </Box>
            <PopoverModal
                open={Boolean(sendModeAnchor)}
                anchorEl={sendModeAnchor}
                onClose={() => setSendModeAnchor(null)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Box
                    sx={{
                        background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        p: 0.5,
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 220,
                    }}
                >
                    <ActionButton
                        onClick={() => { setIsInternal(false); setSendModeAnchor(null); }}
                        sx={{
                            height: 40,
                            px: 1.5,
                            background: !isInternal
                                ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100])
                                : "transparent",
                            justifyContent: "flex-start",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: 1 }}>
                            <PublicIcon sx={{ fontSize: 18, color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400] }} />
                            <Box sx={{ flex: 1, textAlign: "left" }}>
                                <Text fontSize={14} semibold>{publicLabel}</Text>
                            </Box>
                            {!isInternal && <CheckIcon sx={{ fontSize: 16, color: colors.txt["secondary"] }} />}
                        </Box>
                    </ActionButton>
                    <ActionButton
                        onClick={() => { setIsInternal(true); setSendModeAnchor(null); }}
                        sx={{
                            height: 40,
                            px: 1.5,
                            background: isInternal
                                ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100])
                                : "transparent",
                            justifyContent: "flex-start",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: 1 }}>
                            <LockIcon sx={{ fontSize: 18, color: colors.yellow[400] }} />
                            <Box sx={{ flex: 1, textAlign: "left" }}>
                                <Text fontSize={14} semibold>{t("internal_note")}</Text>
                            </Box>
                            {isInternal && <CheckIcon sx={{ fontSize: 16, color: colors.txt["secondary"] }} />}
                        </Box>
                    </ActionButton>
                </Box>
            </PopoverModal>

            {/* Attachments area */}
            {attachments.length > 0 && (
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        pb: 2,
                        pr: 18,
                        borderBottom: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200]}`,
                        mb: 2,
                        flexShrink: 0,
                    }}
                >
                    {attachments.map((attachment, index) => (
                        <Box
                            key={index}
                            sx={{
                                position: "relative",
                                height: 40,
                                px: 1.5,
                                pr: 4,
                                pl:1,
                                borderRadius: 3,
                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            {attachment.type === "image" && attachment.previewUrl && (
                                <Box
                                    component="img"
                                    src={attachment.previewUrl}
                                    alt={attachment.fileName}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleImageClick(index);
                                    }}
                                    sx={{
                                        height: 24,
                                        width: 24,
                                        borderRadius: 2,
                                        objectFit: "cover",
                                        cursor: "pointer",
                                        "&:hover": {
                                            opacity: 0.8,
                                        },
                                    }}
                                />
                            )}
                            <Box
                                sx={{
                                    fontSize: 13,
                                    fontFamily: "Roboto, sans-serif",
                                    maxWidth: 120,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {attachment.fileName}
                            </Box>
                            <IconButton
                                size="small"
                                onClick={() => handleRemoveAttachment(index)}
                                sx={{
                                    position: "absolute",
                                    right: 4,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    width: 20,
                                    height: 20,
                                    color: colors.txt["secondary"],
                                    "&:hover": {
                                        color: colors.txt["primary"],
                                    },
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Editor area */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    // Allow this flex item to shrink below its content size; without
                    // it, a long unbreakable string sets a min-content floor and the
                    // editor pushes wider than its container (maxWidth then "does nothing").
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    // With no attachment row above, the editor starts at the top and
                    // would run under the absolutely-positioned send-mode pill; offset
                    // it down to clear the pill (32px) plus a small gap.
                    pr: attachments.length === 0 ? "200px" : 0,
                    // Target all nested wrappers from TipTap
                    "& > div": {
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        minWidth: 0,
                    },
                    "& .tiptap": {
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        minWidth: 0,
                    },
                    "& .ProseMirror": {
                        flex: 1,
                        outline: "none",
                        minHeight: 0,
                        minWidth: 0,
                        maxWidth: "100%",
                        overflow: "auto",
                        // Wrap long words / URLs instead of overflowing horizontally.
                        whiteSpace: "pre-wrap",
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                        fontSize: 14,
                        fontFamily: "Roboto, sans-serif",
                        color: colors.txt["primary"],
                        "& p": {
                            margin: 0,
                            overflowWrap: "break-word",
                            wordBreak: "break-word",
                        },
                        "&.is-editor-empty:first-child::before": {
                            content: "attr(data-placeholder)",
                            float: "left",
                            color: colors.txt["secondary"],
                            pointerEvents: "none",
                            height: 0,
                        },
                    },
                }}
            >
                <EditorContent editor={editor} />
            </Box>

            {/* Toolbar and actions */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pt: 1.5,
                    flexShrink: 0,
                }}
            >
                {/* Left: formatting toolbar */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>

                    {/* Font size button */}
                    {/* <IconButton
                        size="small"
                        onClick={(e) => setSizeMenuAnchor(e.currentTarget)}
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 2,
                            color: currentSize !== "normal"
                                ? theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]
                                : colors.txt["secondary"],
                            background: currentSize !== "normal"
                                ? theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100]
                                : "transparent",
                            "&:hover": {
                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            },
                        }}
                    >
                        <TextFieldsIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <PopoverModal
                        open={Boolean(sizeMenuAnchor)}
                        anchorEl={sizeMenuAnchor}
                        onClose={() => setSizeMenuAnchor(null)}
                    >
                        <Box
                            sx={{
                                background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                p: 0.5,
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <ActionButton onClick={() => handleSizeSelect("heading")} sx={{ minWidth: 100, height: 32, background: currentSize === "heading" ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]) : "transparent" }}>
                                <Text fontSize={13}>{t("heading")}</Text>
                            </ActionButton>
                            <ActionButton onClick={() => handleSizeSelect("subheading")} sx={{ minWidth: 100, height: 32, background: currentSize === "subheading" ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]) : "transparent" }}>
                                <Text fontSize={13}>{t("subheading")}</Text>
                            </ActionButton>
                            <ActionButton onClick={() => handleSizeSelect("normal")} sx={{ minWidth: 100, height: 32, background: currentSize === "normal" ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]) : "transparent" }}>
                                <Text fontSize={13}>{t("normal")}</Text>
                            </ActionButton>
                        </Box>
                    </PopoverModal> */}

                    {/* Font color button */}
                    {/* <IconButton
                        size="small"
                        onClick={(e) => setColorMenuAnchor(e.currentTarget)}
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 2,
                            color: currentColor !== "primary"
                                ? theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]
                                : colors.txt["secondary"],
                            background: currentColor !== "primary"
                                ? theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100]
                                : "transparent",
                            "&:hover": {
                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            },
                        }}
                    >
                        <FormatColorTextIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <PopoverModal
                        open={Boolean(colorMenuAnchor)}
                        anchorEl={colorMenuAnchor}
                        onClose={() => setColorMenuAnchor(null)}
                    >
                        <Box
                            sx={{
                                background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                p: 0.5,
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <ActionButton onClick={() => handleColorSelect("primary")} sx={{ minWidth: 100, height: 32, background: currentColor === "primary" ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]) : "transparent" }}>
                                <Text fontSize={13}>{t("primary")}</Text>
                            </ActionButton>
                            <ActionButton onClick={() => handleColorSelect("secondary")} sx={{ minWidth: 100, height: 32, background: currentColor === "secondary" ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]) : "transparent" }}>
                                <Text fontSize={13}>{t("secondary")}</Text>
                            </ActionButton>
                        </Box>
                    </PopoverModal> */}
                    
                    {/* Font weight button */}
                    <IconButton
                        size="small"
                        onClick={(e) => setWeightMenuAnchor(e.currentTarget)}
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 2,
                            color: currentWeight !== "normal"
                                ? theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]
                                : colors.txt["secondary"],
                            background: currentWeight !== "normal"
                                ? theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100]
                                : "transparent",
                            "&:hover": {
                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            },
                        }}
                    >
                        <FormatBoldIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <PopoverModal
                        open={Boolean(weightMenuAnchor)}
                        anchorEl={weightMenuAnchor}
                        onClose={() => setWeightMenuAnchor(null)}
                    >
                        <Box
                            sx={{
                                background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                p: 0.5,
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <ActionButton onClick={() => handleWeightSelect("black")} sx={{ minWidth: 100, height: 32, background: currentWeight === "black" ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]) : "transparent" }}>
                                <Text fontSize={13}>{t("black")}</Text>
                            </ActionButton>
                            <ActionButton onClick={() => handleWeightSelect("bold")} sx={{ minWidth: 100, height: 32, background: currentWeight === "bold" ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]) : "transparent" }}>
                                <Text fontSize={13}>{t("bold")}</Text>
                            </ActionButton>
                            <ActionButton onClick={() => handleWeightSelect("semibold")} sx={{ minWidth: 100, height: 32, background: currentWeight === "semibold" ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]) : "transparent" }}>
                                <Text fontSize={13}>{t("semibold")}</Text>
                            </ActionButton>
                            <ActionButton onClick={() => handleWeightSelect("normal")} sx={{ minWidth: 100, height: 32, background: currentWeight === "normal" ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]) : "transparent" }}>
                                <Text fontSize={13}>{t("normal")}</Text>
                            </ActionButton>
                        </Box>
                    </PopoverModal>

                    {/* Italic */}
                    <IconButton
                        size="small"
                        onClick={toggleItalic}
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 2,
                            color: isItalic
                                ? theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]
                                : colors.txt["secondary"],
                            background: isItalic
                                ? theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100]
                                : "transparent",
                            "&:hover": {
                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            },
                        }}
                    >
                        <FormatItalicIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    
                    <IconButton
                        size="small"
                        onClick={handleAttachmentClick}
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 2,
                            color: colors.txt["secondary"],
                            "&:hover": {
                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            },
                        }}
                    >
                        <AttachFileIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                {/* Right: clear and send */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                        size="small"
                        onClick={handleClear}
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 2,
                            color: colors.txt["secondary"],
                            "&:hover": {
                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                color: colors.red[400],
                            },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    {!hideSend && (
                        // Single rounded container so send + chevron read as one
                        // split button; the buttons inside are square and clipped.
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                borderRadius: 2,
                                overflow: "hidden",
                                background: colors.primary[400],
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={() => handleSend(false)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 0,
                                    color: "#fff",
                                    "&:hover": {
                                        background: colors.primary[300],
                                    },
                                }}
                            >
                                <SendIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            {allowResolveOnSend && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => setSendActionAnchor(e.currentTarget)}
                                    sx={{
                                        width: 22,
                                        height: 32,
                                        borderRadius: 0,
                                        borderLeft: "1px solid rgba(255,255,255,0.35)",
                                        color: "#fff",
                                        "&:hover": {
                                            background: colors.primary[300],
                                        },
                                    }}
                                >
                                    <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            )}
                        </Box>
                    )}
                    {!hideSend && allowResolveOnSend && (
                            <PopoverModal
                                open={Boolean(sendActionAnchor)}
                                anchorEl={sendActionAnchor}
                                onClose={() => setSendActionAnchor(null)}
                                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                                transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                            >
                                <Box
                                    sx={{
                                        background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                        p: 0.5,
                                        display: "flex",
                                        flexDirection: "column",
                                        minWidth: 220,
                                    }}
                                >
                                    <ActionButton
                                        onClick={() => { setSendActionAnchor(null); handleSend(false); }}
                                        sx={{ height: 40, px: 1.5, justifyContent: "flex-start" }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: 1 }}>
                                            <SendIcon sx={{ fontSize: 18, color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400] }} />
                                            <Box sx={{ flex: 1, textAlign: "left" }}>
                                                <Text fontSize={14} semibold>{t("send")}</Text>
                                            </Box>
                                        </Box>
                                    </ActionButton>
                                    <ActionButton
                                        onClick={() => { setSendActionAnchor(null); handleSend(true); }}
                                        sx={{ height: 40, px: 1.5, justifyContent: "flex-start" }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: 1 }}>
                                            <CheckCircleOutlineIcon sx={{ fontSize: 18, color: colors.green[400] }} />
                                            <Box sx={{ flex: 1, textAlign: "left" }}>
                                                <Text fontSize={14} semibold>{t("send_and_resolve")}</Text>
                                            </Box>
                                        </Box>
                                    </ActionButton>
                                </Box>
                            </PopoverModal>
                    )}
                </Box>
            </Box>

            {/* Photo viewer modal */}
            <MPhotoViewer
                open={photoViewerOpen}
                onClose={() => setPhotoViewerOpen(false)}
                photos={photosForViewer}
                currentPhotoIndex={currentPhotoIndex}
                onPhotoChange={setCurrentPhotoIndex}
                onDelete={handlePhotoViewerDelete}
            />
        </Box>
    );
};

export default ChatInput;
